import { isAdmin } from '@/utils/adminProRedirect'
import clsx from 'clsx'
import type { AdminDetails } from 'lib/types/mock/admin-details'
import { generateSnapshotShipmentLeg } from 'lib/utils/routeSnapshotUtils'
import { ReactNode, useMemo } from 'react'
import CollapsibleSection from '../CollapsibleSection/CollapsibleSection'
import CruiseDetails from '../CruiseDetails/CruiseDetails'
import Header from '../Header/Header'
import OriginDestinationAddress from '../OriginDestinationAddress/OriginDestinationAddress'
import OriginDestinationHeader from '../OriginDestinationHeader/OriginDestinationHeader'
import ServiceProviderLogo from '../ServiceProviderLogo/ServiceProviderLogo'
import RouteSnapshotShipment, {
  RouteSnapshotShipmentProps,
} from './partials/RouteSnapshotShipment'

export interface RouteSnapshotTravelerShipment {
  traveler: string
  shipments: OrderShipment[]
}

export interface RouteSnapshotButtonHandlers {
  printShipment?: (trackingId: string) => void
}

export interface RouteSnapshotProps {
  /**
   * Whether the route is Outbound or Return
   */
  type: LegType
  /**
   * The carrier service provider ID
   */
  shipper: Shipper
  /**
   * The origin address
   */
  origin: Partial<OrderAddress>
  /**
   * The destination address
   */
  destination: Partial<OrderAddress>
  /**
   * Shipment Legs. Each leg contains the traveler and shipments (products) data
   */
  shipments: RouteSnapshotTravelerShipment[]
  /**
   * Optional callback to format the URL for the tracking ID link
   */
  formatTrackingIdLink?: RouteSnapshotShipmentProps['formatTrackingIdLink']
  /**
   * Legal text at the bottom
   */
  legal_text?: string | ReactNode
  /**
   * Click handlers for specific buttons in the component: Print shipment
   */
  buttonHandlers?: RouteSnapshotButtonHandlers
  /**
   * Callback to get the full shipment details for a given tracking ID.
   * Shipment details should be formatted to match ShipmentDetailsModal props
   */
  getShipmentDetails?: RouteSnapshotShipmentProps['getShipmentDetails']

  onClickShipmentDetails?: () => void // Optional for custom handling
  /**
   * Optional: Determine whether user is authenticated
   */
  isUserAuthenticated?: boolean
  /**
   * Optional: Details provided via Admin mode
   */
  adminDetails?: DeepPartial<AdminDetails>[]
  /**
   * Optional: Determine whether shipment involves a cruise
   */
  isCruise?: boolean
  options?: Record<string, any>
  activeShipmentId?: string
  pickup?: boolean
  /**
   * Whether the leg is an Uber pickup
   */
  uberPickup?: boolean
  /**
   * ISO timestamp for Uber pickup window start
   */
  pickupReadyTime?: string
  /**
   * ISO timestamp for Uber pickup window end
   */
  pickupCloseTime?: string
  /**
   * Optional: Order user ID for permission checks
   */
  orderUserId?: string
  /**
   * Optional: Current logged in user ID for permission checks
   */
  currentUserId?: string | null
  /**
   * Optional: Current user roles for permission checks
   */
  userRoles?: string[]
  /**
   * Optional: Allow unauthenticated guests to see the print label button
   */
  allowGuestPrintLabel?: boolean
}

const defaultOptions = {
  showShipmentHeader: true,
  showShipper: true,
  showLegDetails: true,
  showShipmentDetails: true,
  shipmentContainerBackground: 'bg-neutral-200',
  shipmentContainerClass: '',
  // No inner card: the shipment sits directly on the section background
  // instead of nesting a second panel inside it.
  shipmentClass: 'p-0',
  shipmentContainerPadding: 'p-6 lg:p-8',
}

export default function RouteSnapshot({
  type = 'outbound',
  shipper,
  origin,
  destination,
  shipments,
  formatTrackingIdLink = (tracking) => `#${tracking}`,
  legal_text,
  buttonHandlers,
  getShipmentDetails,
  onClickShipmentDetails,
  isUserAuthenticated = false,
  activeShipmentId,
  adminDetails,
  isCruise = false,
  options = {},
  pickup = false,
  uberPickup,
  pickupReadyTime,
  pickupCloseTime,
  orderUserId,
  currentUserId,
  userRoles,
  allowGuestPrintLabel,
}: RouteSnapshotProps) {
  const customOptions = useMemo(() => {
    return { ...defaultOptions, ...options }
  }, [options])

  const leg = useMemo(
    () =>
      generateSnapshotShipmentLeg(type, origin, destination, shipments, pickup),
    [type, origin, destination, shipments, pickup],
  )

  const detailNotes = useMemo(
    () => adminDetails?.map((detail) => detail.notes).flat() || [],
    [adminDetails],
  )

  const canViewAddresses = useMemo(() => {
    if (!orderUserId) {
      return false
    }

    if (!currentUserId) {
      return false
    }

    const isOrderUser = currentUserId === orderUserId

    const userIsAdmin = isAdmin(userRoles)

    return isOrderUser || userIsAdmin
  }, [currentUserId, orderUserId, userRoles])

  // A leg's cruise end is whichever point is a ship/port: the destination on an
  // outbound (to-cruise) leg, the origin on a return (from-cruise) leg. Either
  // may be flagged; fall back to the explicit prop for callers that know.
  const destinationIsCruise = isCruise || Boolean(destination.cruise)
  const originIsCruise = Boolean(origin.cruise)

  // Cruise session (cabin, reservation #, etc.) hangs off the shipment, not the
  // address — grab the first shipment that carries one.
  const cruiseSession = useMemo(
    () =>
      shipments
        .flatMap((travelerShipment) => travelerShipment.shipments)
        .find((shipment) => shipment?.cruise_session)?.cruise_session ?? null,
    [shipments],
  )

  return (
    <div
      className={clsx(
        'lg:rounded-8',
        customOptions?.shipmentContainerPadding || 'p-6 lg:p-8',
        customOptions?.shipmentContainerBackground || 'bg-neutral-200',
      )}
    >
      {/* Two columns: the leg's shipments fill the left, with the route and
          the cruise itinerary stacked in a column on the right. */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-10">
        <div className="lg:flex-1 lg:min-w-0">
          {customOptions.showShipmentHeader && (
            <header className="flex gap-x-4 items-center">
              {customOptions.showLegDetails && (
                <Header as="h4" className="type-body-1">
                  {type === 'return' ? 'Return' : 'Outbound'} details
                </Header>
              )}
              {customOptions.showShipper && (
                <ServiceProviderLogo
                  provider={shipper}
                  className="[&]:h-5"
                  aria-label={`Shipment carrier: ${shipper}`}
                />
              )}
            </header>
          )}
          <div className="flex flex-col gap-y-6 mt-4">
            {shipments.map(
              ({ traveler, shipments: travelerShipments }, shipmentIndex) => (
                <div
                  key={shipmentIndex}
                  className="[&:not(:last-child)]:border-b border-neutral-600"
                >
                  {customOptions.showShipmentDetails && (
                    <p className="type-headline-4 lg:type-body-2-desktop">
                      {traveler}
                    </p>
                  )}
                  <div
                    className={
                      customOptions.showShipmentDetails ? 'mt-4' : 'mt-0'
                    }
                  >
                    {travelerShipments.map(
                      (shipment, travelerShipmentIndex) => (
                        <div
                          key={travelerShipmentIndex}
                          className={customOptions.shipmentContainerClass || ''}
                        >
                          <RouteSnapshotShipment
                            leg={leg}
                            shipment={shipment}
                            uberPickup={uberPickup}
                            pickupReadyTime={pickupReadyTime}
                            pickupCloseTime={pickupCloseTime}
                            originTimeZone={origin?.time_zone}
                            formatTrackingIdLink={formatTrackingIdLink}
                            buttonHandlers={buttonHandlers}
                            getShipmentDetails={getShipmentDetails}
                            onClickShipmentDetails={onClickShipmentDetails}
                            adminDetail={
                              adminDetails &&
                              (adminDetails.find(
                                (adminDetail) =>
                                  adminDetail === shipment.tracking_id,
                              ) ??
                                {})
                            }
                            isUserAuthenticated={isUserAuthenticated}
                            activeShipmentId={activeShipmentId}
                            options={customOptions}
                            orderUserId={orderUserId}
                            currentUserId={currentUserId}
                            userRoles={userRoles}
                            allowGuestPrintLabel={allowGuestPrintLabel}
                          />
                        </div>
                      ),
                    )}
                  </div>
                </div>
              ),
            )}
          </div>
        </div>

        <div
          className={clsx(
            'flex flex-col gap-4',
            // Stacked on mobile the two halves need a rule to read as separate
            // sections; side by side at lg the column gap already does that, so
            // no vertical divider.
            'border-t border-neutral-400 pt-4 mt-2',
            'lg:w-[320px] lg:shrink-0 lg:border-t-0 lg:pt-0 lg:mt-0',
          )}
        >
          <div>
            {canViewAddresses && isUserAuthenticated ? (
              <OriginDestinationAddress
                // Headings label the stacked layout; from `md` the arrow
                // between the two columns carries the direction instead. The
                // component picks the correct arrow for its own breakpoint, so
                // there are no icon overrides here.
                className="[&]:pt-0 [&]:border-0 md:[&_h4]:hidden"
                origin={[
                  origin.company_name || origin.attention_name,
                  origin.address_1,
                  origin.address_2,
                  `${origin.city}, ${origin.state}`,
                  `${origin.zip}, ${origin.country}`,
                ].filter((value) => typeof value === 'string')}
                destination={[
                  destination.company_name || destination.attention_name,
                  destination.address_1,
                  destination.address_2,
                  `${destination.city}, ${destination.state}`,
                  `${destination.zip}, ${destination.country}`,
                ].filter((value) => typeof value === 'string')}
              />
            ) : (
              <OriginDestinationHeader
                as="h4"
                origin={[origin.city, origin.state?.toUpperCase()].join(', ')}
                destination={[
                  destination.city,
                  destination.state?.toUpperCase(),
                ].join(', ')}
                iconClassName="text-accessible-green"
              />
            )}
          </div>

          {(originIsCruise || destinationIsCruise) && cruiseSession ? (
            <CruiseDetails
              cruise={cruiseSession}
              portName={
                destinationIsCruise
                  ? cruiseSession?.embarkation_port
                  : cruiseSession?.disembarkation_port
              }
            />
          ) : null}
        </div>
      </div>
      {!isCruise && adminDetails && (
        <CollapsibleSection
          title="Shipment notes"
          sectionClassName="[&]:bg-transparent [&]:border-0 mb-4 md:mb-8"
          buttonClassName="[&]:px-0 [&]:py-4 [&]:md:py-6 [&]:type-body-5"
          panelClassName="[&]:px-0"
        >
          <ul>
            {detailNotes?.map((note: any, noteIndex: number) => (
              <li className="mb-4 last:mb-0 type-body-8" key={noteIndex}>
                <span className="font-semibold type-body-8 md:type-body-9">
                  [{note.created_at}]
                </span>{' '}
                {note.comment} -{note.author_name}
              </li>
            ))}
          </ul>
        </CollapsibleSection>
      )}
      {legal_text ? (
        <p className="type-body-8 text-neutral-600">{legal_text}</p>
      ) : null}
    </div>
  )
}
